/**
 * Who is signed in, as the server sees it. The single client-side answer: the
 * route guard, the sidebar's account row and Settings all read this.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { signOut } from '@/adapters/auth/client'
import { orpc } from '@/adapters/rpc/client'

/**
 * Cached for a minute, so moving between screens does not re-ask the server on
 * every navigation, but a session that expires is noticed promptly.
 */
export const meQueryOptions = () => ({
  ...orpc.me.identity.queryOptions(),
  staleTime: 60_000,
  retry: false,
})

export function useMe(options: { enabled?: boolean } = {}) {
  return useQuery({ ...meQueryOptions(), enabled: options.enabled ?? true })
}

/** "Gideon Harris" → "GH". */
export function initialsOf(name: string, email: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters =
    words.length >= 2 ? `${words[0]?.[0]}${words.at(-1)?.[0]}` : (words[0] ?? email).slice(0, 2)
  return letters.toUpperCase()
}

/**
 * Sign out, then forget everything cached. Clearing the whole query cache
 * matters: without it the next person to sign in on the same browser could
 * briefly see the previous person's records before their own arrived.
 */
export function useSignOut() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return useCallback(async () => {
    try {
      await signOut()
    } catch {
      // In mock mode there is no auth server to tell; signing out is a no-op.
    }
    queryClient.clear()
    await navigate({ to: '/sign-in' })
  }, [queryClient, navigate])
}
